/**
 * Web Push subscription helper (browser side).
 *
 * Flow:
 *   1. Register `/push-sw.js` as a service worker.
 *   2. Ask the user for notification permission.
 *   3. Fetch the VAPID public key from the backend.
 *   4. Call `pushManager.subscribe` and POST the resulting subscription to
 *      `/api/admin/push/subscribe` so the backend can push to it later.
 *
 * Every step is defensive: unsupported browsers (Safari on non-HTTPS, older
 * Firefox on Android, in-app webviews) return a helpful `{ok:false, reason}`
 * instead of throwing, so the UI can render a nice error.
 */
import api from '@/lib/api';

const SW_PATH = '/push-sw.js';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

export const currentPushPermission = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

export const registerServiceWorker = async () => {
  if (!pushSupported()) throw new Error('Push notifications não são suportadas neste browser.');
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH);
};

export const getExistingSubscription = async () => {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

/** Subscribe (or reuse an existing subscription) and register with backend. */
export const enablePush = async () => {
  if (!pushSupported()) {
    return { ok: false, reason: 'unsupported', message: 'O browser não suporta notificações push.' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied', message: 'Permissão de notificação recusada.' };
  }

  let vapidKey;
  try {
    const res = await api.get('/notifications/push/vapid-key');
    vapidKey = res.data?.public_key;
  } catch (err) {
    return { ok: false, reason: 'vapid_missing', message: 'Servidor sem chave VAPID configurada.' };
  }
  if (!vapidKey) return { ok: false, reason: 'vapid_missing', message: 'Servidor sem chave VAPID.' };

  const reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const raw = sub.toJSON();
  await api.post('/admin/push/subscribe', {
    endpoint: raw.endpoint,
    keys: raw.keys,
    userAgent: navigator.userAgent,
  });

  return { ok: true, endpoint: raw.endpoint };
};

/** Unsubscribe on both browser + backend so we don't leak dead endpoints. */
export const disablePush = async () => {
  if (!pushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) { /* browser refused — still cleanup backend */ }
  try {
    await api.delete('/admin/push/unsubscribe', { params: { endpoint } });
  } catch (e) { /* backend may already have expired it */ }
  return { ok: true };
};

/** Fire a test notification through the backend so the user can confirm setup. */
export const sendTestPush = async () => {
  const res = await api.post('/admin/push/test');
  return res.data;
};
